import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type GetPaths } from '@/api/client';

/**
 * Shared plumbing for the HR & Payroll screens: the shapes the API returns,
 * one fetch helper, and the lookups several screens need.
 *
 * Pay figures and personal numbers are optional on every type here because
 * the server leaves them out entirely for anyone without the salary or
 * personal-details permission. A missing field is "not yours to see", never
 * zero — screens show a dash for it.
 */

type Query = Record<string, string | number | boolean | undefined | null>;

/** GET an HR route. The paths are in the OpenAPI spec, but their responses
 *  are described here rather than there, so the cast is deliberate. */
export async function hrGet<T>(path: string, query?: Query): Promise<T> {
  const clean: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') clean[k] = typeof v === 'boolean' ? String(v) : v;
  }
  return (await api.get(path as GetPaths, clean as never)) as unknown as T;
}

/** Everything HR caches starts with 'hr'; saving anything refreshes it all. */
export function useHrInvalidation() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['hr'] });
}

// ------------------------------------------------------------ lookups

export interface Named {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
}

export interface PayComponentOption extends Named {
  kind: 'EARNING' | 'DEDUCTION' | 'EMPLOYER';
  systemCode: string | null;
  taxable: boolean;
  countsForOvertime: boolean;
  percentOfBasic: string | null;
  sortOrder: number;
}

export interface Lookups {
  departments: Named[];
  designations: Named[];
  shifts: Array<Named & { startTime: string; endTime: string; isNight: boolean }>;
  leaveTypes: Array<Named & { isPaid: boolean; forDailyWorkers: boolean }>;
  payComponents: PayComponentOption[];
}

export function useHrLookups(enabled = true) {
  return useQuery({
    queryKey: ['hr', 'lookups'],
    queryFn: () => hrGet<Lookups>('/api/v1/hr/lookups'),
    staleTime: 60_000,
    enabled,
  });
}

export interface FarmOption {
  id: string;
  code: string;
  nameEn: string;
  isActive: boolean;
}

/** Farms the caller can see, for farm pickers and the farm tree. */
export function useFarmOptions() {
  return useQuery({
    queryKey: ['farms', 'list', false],
    queryFn: async () => (await api.get('/api/v1/farms', {})) as unknown as FarmOption[],
    staleTime: 60_000,
  });
}

export interface ShedOption {
  id: string;
  code: string;
  nameEn: string;
  farmId: string;
}

export function useShedOptions(farmId: string | null | undefined) {
  return useQuery({
    queryKey: ['sheds', farmId, false],
    queryFn: async () => (await api.get('/api/v1/sheds', { farmId: farmId! })) as unknown as ShedOption[],
    enabled: !!farmId,
    staleTime: 60_000,
  });
}

// ------------------------------------------------------------ employees

export type WorkerType = 'MONTHLY' | 'DAILY';
export type PaymentMethod = 'CASH' | 'BANK' | 'WALLET';
export type EmployeeStatus = 'ACTIVE' | 'SUSPENDED' | 'LEFT';

export interface EmployeeRow {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  fatherName: string | null;
  cnic?: string | null;
  contactNo: string | null;
  workerType: WorkerType;
  payFrequency: 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';
  status: EmployeeStatus;
  farmId: string | null;
  farmCode: string | null;
  farmName: string | null;
  shedId: string | null;
  shedName: string | null;
  departmentId: string | null;
  department: string | null;
  designationId: string | null;
  designation: string | null;
  joinedOn: string;
  leftOn: string | null;
  paymentMethod: PaymentMethod;
  salary?: string | null;
  dailyRate?: string | null;
  flags: string[];
}

export interface PayLine {
  componentId: string;
  code: string;
  nameEn: string;
  payAmount?: string | null;
}

export interface CurrentPay {
  revisionId: string;
  documentNo: string;
  effectiveFrom: string;
  monthlyGross?: string | null;
  dailyRate?: string | null;
  lines: PayLine[];
}

export interface PayRevision {
  id: string;
  documentNo: string;
  employeeId: string;
  effectiveFrom: string;
  monthlyGross?: string | null;
  dailyRate?: string | null;
  reason: string;
  isInitial: boolean;
  approval: 'PENDING' | 'APPROVED' | 'REJECTED';
  decidedAt: string | null;
  decisionNote: string | null;
  raisedByYou: boolean;
  createdAt: string;
  lines: PayLine[];
  employeeCode?: string;
  employeeName?: string;
  salary?: string | null;
}

export interface EmployeeDetail extends EmployeeRow {
  cnicExpiry?: string | null;
  dateOfBirth?: string | null;
  gender: string | null;
  otherContact: string | null;
  homeAddress?: string | null;
  emergencyName?: string | null;
  emergencyPhone?: string | null;
  reportsToId: string | null;
  reportsTo: { id: string; code: string; nameEn: string } | null;
  shiftId: string | null;
  shift: string | null;
  weeklyOff: number[];
  userId: string | null;
  user: { id: string; userId: string; fullName: string } | null;
  probationEnd: string | null;
  leftReason: string | null;
  leftNote: string | null;
  overtimeEligible: boolean;
  payBankName?: string | null;
  payAccountTitle?: string | null;
  payIban?: string | null;
  walletProvider: string | null;
  walletNo?: string | null;
  eobiRegistered: boolean;
  eobiNo?: string | null;
  pessiRegistered: boolean;
  pessiNo?: string | null;
  taxApplies: boolean;
  ntn: string | null;
  openingTaxYear: number | null;
  openingTaxableYtd?: string | null;
  openingTaxYtd?: string | null;
  pay: CurrentPay | null;
  pendingRevision: PayRevision | null;
  assignments: Array<{ id: string; farm: string; shed: string | null; fromDate: string; toDate: string | null }>;
  recurringItems: Array<{
    id: string;
    componentId: string;
    component: string;
    kind: string;
    payAmount?: string | null;
    fromDate: string;
    toDate: string | null;
    note: string | null;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeEvent {
  id: string;
  type: string;
  eventDate: string;
  detail: string | null;
  createdAt: string;
}

/** What each attention flag means, in plain words. */
export const FLAG_TEXT: Record<string, string> = {
  CNIC_MISSING: 'No CNIC on file',
  CNIC_EXPIRING: 'CNIC expired or expiring soon',
  PROBATION_ENDING: 'Probation ends this month',
  PAYMENT_DETAILS_MISSING: 'Bank or wallet details missing',
  NO_PAY: 'No approved pay',
  BELOW_MINIMUM_WAGE: 'Paid below the minimum wage',
};

export const WORKER_TYPE_TEXT: Record<WorkerType, string> = {
  MONTHLY: 'Monthly salary',
  DAILY: 'Daily wage',
};

export const PAY_METHOD_TEXT: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  BANK: 'Bank transfer',
  WALLET: 'Mobile wallet',
};

export const FREQUENCY_TEXT: Record<string, string> = {
  MONTHLY: 'Monthly',
  FORTNIGHTLY: 'Fortnightly',
  WEEKLY: 'Weekly',
};

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const LEFT_REASON_TEXT: Record<string, string> = {
  RESIGNED: 'Resigned',
  TERMINATED: 'Let go',
  ABSCONDED: 'Stopped coming',
  RETIRED: 'Retired',
  DECEASED: 'Passed away',
  OTHER: 'Other',
};

/** Picker of people, for leave, advances and settlements. */
export function useEmployeeOptions(status: 'CURRENT' | 'LEFT' = 'CURRENT', enabled = true) {
  return useQuery({
    queryKey: ['hr', 'employees', 'options', status],
    queryFn: () => hrGet<{ total: number; items: EmployeeRow[] }>('/api/v1/hr/employees', { status, take: 500 }),
    staleTime: 30_000,
    enabled,
  });
}

export function employeeLabel(e: { code: string; nameEn: string; farmCode?: string | null }): string {
  return `${e.code} — ${e.nameEn}${e.farmCode ? ` (${e.farmCode})` : ''}`;
}

// ------------------------------------------------------------ attendance

export type Mark = 'PRESENT' | 'ABSENT' | 'HALF_DAY' | 'LEAVE' | 'WEEKLY_OFF' | 'HOLIDAY';

export const MARK_TEXT: Record<Mark, string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  HALF_DAY: 'Half day',
  LEAVE: 'Leave',
  WEEKLY_OFF: 'Weekly off',
  HOLIDAY: 'Holiday',
};

/** One-letter codes used on the register and the month view. */
export const MARK_CODE: Record<Mark, string> = {
  PRESENT: 'P',
  ABSENT: 'A',
  HALF_DAY: 'H',
  LEAVE: 'L',
  WEEKLY_OFF: 'O',
  HOLIDAY: 'HD',
};

// ------------------------------------------------------------ money in words

const ONES = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function belowHundred(n: number): string {
  if (n < 20) return ONES[n]!;
  return `${TENS[Math.floor(n / 10)]}${n % 10 ? `-${ONES[n % 10]}` : ''}`;
}

function belowThousand(n: number): string {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return [h ? `${ONES[h]} hundred` : '', rest ? belowHundred(rest) : ''].filter(Boolean).join(' ');
}

/**
 * Rupees in words the Pakistani way — crore, lakh, thousand — as printed on
 * a payslip or cheque: 153,250 → "One lakh fifty-three thousand two hundred
 * and fifty rupees only". Paisa are written out too — 999.60 is "…rupees
 * and sixty paisa only" — so the words always match the figure beside them.
 */
export function amountInWords(value: string | number | null | undefined): string {
  const total = Math.round(Math.abs(Number(value ?? 0)) * 100 * (1 + Number.EPSILON));
  if (!Number.isFinite(total)) return '';
  const n = Math.floor(total / 100);
  const paisa = total % 100;
  const paisaText = paisa ? `${belowHundred(paisa)} paisa` : '';
  if (n === 0) {
    if (!paisa) return 'Zero rupees only';
    return `${paisaText.charAt(0).toUpperCase()}${paisaText.slice(1)} only`;
  }
  const crore = Math.floor(n / 10_000_000);
  const lakh = Math.floor((n % 10_000_000) / 100_000);
  const thousand = Math.floor((n % 100_000) / 1000);
  const rest = n % 1000;
  const parts: string[] = [];
  if (crore) parts.push(`${crore < 1000 ? belowThousand(crore) : String(crore)} crore`);
  if (lakh) parts.push(`${belowHundred(lakh)} lakh`);
  if (thousand) parts.push(`${belowHundred(thousand)} thousand`);
  if (rest) {
    const h = Math.floor(rest / 100);
    const r = rest % 100;
    if (h) parts.push(`${ONES[h]} hundred`);
    if (r) parts.push(`${parts.length ? 'and ' : ''}${belowHundred(r)}`);
  }
  const text = `${parts.join(' ')} ${n === 1 ? 'rupee' : 'rupees'}${paisa ? ` and ${paisaText}` : ''} only`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ------------------------------------------------------------ dates

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function monthName(month: number): string {
  return MONTH_NAMES[month - 1] ?? '';
}

/** "September 2026" for a whole month, otherwise "1 Sep – 7 Sep 2026". */
export function periodLabel(start: string, end: string): string {
  const [ys, ms, ds] = start.split('-').map(Number);
  const [ye, me, de] = end.split('-').map(Number);
  const last = new Date(Date.UTC(ye!, me!, 0)).getUTCDate();
  if (ds === 1 && de === last && ys === ye && ms === me) return `${monthName(ms!)} ${ys}`;
  const short = (m: number) => monthName(m).slice(0, 3);
  return `${ds} ${short(ms!)}${ys !== ye ? ` ${ys}` : ''} – ${de} ${short(me!)} ${ye}`;
}

/** First and last day of a month, as YYYY-MM-DD. */
export function monthBounds(year: number, month: number): { start: string; end: string } {
  const mm = String(month).padStart(2, '0');
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { start: `${year}-${mm}-01`, end: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

/** "2026-27" for the tax year starting in July 2026. */
export function taxYearLabel(taxYear: number): string {
  return `${taxYear}-${String((taxYear + 1) % 100).padStart(2, '0')}`;
}

/** The tax year (by its July year) a date falls in. */
export function taxYearOf(date: string): number {
  const [y, m] = date.split('-').map(Number);
  return m! >= 7 ? y! : y! - 1;
}

export function addDaysIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
